import React, {
  ReactNode,
  RefObject,
  useCallback,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { Outlet } from 'react-router-dom';
import SidebarHeader from './SidebarHeader';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSidebar } from '@fortawesome/pro-light-svg-icons';

interface Props {
  leftSideBar: any;
}

interface ContextProps {
  setRightSidebarContent: (element: ReactNode | undefined) => void;
}

const Context = React.createContext<ContextProps>({
  setRightSidebarContent: () => {},
});

export const useSidebarLayout = () => React.useContext(Context);

const Provider = ({
  children,
  containerRef,
}: { children: any; containerRef: RefObject<HTMLDivElement> }) => {
  const [portal, setPortal] = React.useState<ReactNode | null>(null);

  const setRightSidebarContent = useCallback(
    (element: ReactNode | null) => {
      if (containerRef.current) {
        if (element) {
          // Display
          containerRef.current.style.width = '0px';
          containerRef.current.style.display = 'flex';
          setTimeout(() => {
            if (containerRef.current)
              containerRef.current.style.width = '400px';
            setTimeout(() => {
              if (containerRef.current)
                (
                  containerRef.current.children[0] as HTMLDivElement
                ).style.opacity = '1';
            }, 150);
          });
          setPortal(
            createPortal(
              <div className="h-full overflow-y-auto w-full transition-opacity opacity-0">
                {element}
              </div>,
              containerRef.current,
            ),
          );
        } else {
          // Hide
          containerRef.current.style.width = '0px';
          (containerRef.current.children[0] as HTMLDivElement).style.opacity =
            '0';
          setTimeout(() => {
            if (containerRef.current) {
              containerRef.current.style.display = 'none';
            }
            setPortal(null);
          }, 150);
        }
      }
    },
    [containerRef],
  );

  const providerValue = useMemo(
    () => ({
      setRightSidebarContent,
    }),
    [setRightSidebarContent],
  );

  return (
    <Context.Provider value={providerValue}>
      {children}
      {portal}
    </Context.Provider>
  );
};

const SidebarLayout = ({ leftSideBar }: Props) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  return (
    <Provider containerRef={containerRef}>
      <div className="flex flex-row flex-auto w-full overflow-hidden">
        <div
          className="lg:w-[300px] border-r-[#efefef] border-r bg-white h-screen lg:h-auto fixed top-0 left-0 z-[3000] lg:relative lg:z-auto flex lg:!flex flex-col shadow-xl w-[90%] max-w-[300px] lg:shadow-none"
          style={{
            display: isSidebarOpen ? 'flex' : 'none',
          }}
          onClick={() => setIsSidebarOpen(false)}
        >
          <SidebarHeader
            className="lg:hidden"
            onClose={() => setIsSidebarOpen(false)}
          />

          <div className="h-full overflow-y-auto w-full">{leftSideBar}</div>
        </div>
        <div className="flex-1 overflow-hidden bg-[#f8f8f8]">
          <div className="h-full overflow-y-auto w-full" id="contentContainer">
            <Outlet />
          </div>
        </div>
        <div
          className="border-l border-l-[#efefef] bg-white hidden transition-width h-screen lg:h-auto fixed top-0 right-0 z-[3000] lg:relative lg:z-auto flex-col shadow-xl w-[90%] max-w-[300px] lg:shadow-none"
          ref={containerRef}
        />
      </div>
      <div
        className="fixed top-2 left-0 flex items-center justify-start w-14 pl-5 z-[1501] h-[40px] lg:hidden cursor-pointer"
        onClick={() => setIsSidebarOpen(true)}
      >
        <FontAwesomeIcon icon={faSidebar} size="lg" />
      </div>
    </Provider>
  );
};

export default SidebarLayout;
